import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { User } from '../../models/user';
import { catchError, distinctUntilChanged, filter, finalize, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { BaseApiService } from '../../services/base-api-service/base-api.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TokenService } from '../token-service/token.service';
import { environment } from '../../../../environments/environment';
import { ApiStandardReturn } from '../../models/api-standard-return';

const TIMEOUT = 200; // milliseconds

/**
 * Stores information about the user.
 */
@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseApiService {
    // Injectables
    router = inject(Router);
    dialog = inject(MatDialog);
    private tokenService = inject(TokenService);

    private readonly user$: Observable<User | null>;

    // Hide writable signal from consumer
    private _user: WritableSignal<User | null> = signal(null);
    private _loadingUser: WritableSignal<boolean> = signal(false);

    // Expose non-writable signals
    user = this._user.asReadonly();
    loadingUser = this._loadingUser.asReadonly();

    constructor() {
        super();

        this.user$ = this.tokenService.tokenReady$.pipe(
            distinctUntilChanged(),
            filter(ready => ready),
            switchMap(() => {
                this._loadingUser.set(true);
                const path = '/v1/auth';
                return this.http.get<ApiStandardReturn<User>>(environment.apiUrl + path).pipe(
                    take(1),
                    map(response => response.data),
                    tap(user => this._user.set(user)),
                    catchError(() => {
                        const route = '/unauthorised';
                        this.router.navigate([route]);
                        return of(this.user());
                    }),
                    finalize(() => {
                        setTimeout(() => this._loadingUser.set(false), TIMEOUT);
                    }),
                );
            }),
            shareReplay(1),
        );
        this.user$.subscribe();
    }

    /**
     * Retrieves user observable.
     * @return Observable of the user
     */
    getUser(): Observable<User | null> {
        return this.user$;
    }
}
